import { Box, CircularProgress, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, IconButton, Menu, MenuItem } from "@mui/material";
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faEye } from '@fortawesome/free-solid-svg-icons';
import TablePaginationActions from '../../../base-components/TablePaginationActions';
import CustomModal from '../../../base-components/CustomModal';
import Popup from '../../../base-components/Popup';
import Tooltip from '@mui/material/Tooltip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { encodedImagesURI } from '../../../utils/helpers';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const categories = ['Sports Gear', 'Tracksuits', 'Shirts', 'Trousers', 'Accessories', 'Services'];

function Products({ company_id, location_id, role }) {
  const [products, setProducts] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState('');
  const [productToUpdate, setProductToUpdate] = useState(null);
  const [productToView, setProductToView] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [isSuccessAlert, setIsSuccessAlert] = useState(false);
  const [isFailureAlert, setIsFailureAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [failureMessage, setFailureMessage] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const openAnchor = Boolean(anchorEl);

  useEffect(() => {
    if (company_id && location_id) {
      getProducts();
    }
  }, [company_id, location_id, selectedCategory]);

  const getProducts = async () => {
    setLoading(true);
    let url = `${BASE_URL}products?company_id=${company_id}&location_id=${location_id}`;
    if (selectedCategory) {
      url += `&category=${selectedCategory}`;
    }
    try {
      const response = await fetch(url);
      const data = await response.json();
      if (response.ok) {
        // Handle both array response and object with products property
        if (Array.isArray(data)) {
          setProducts(data);
        } else {
          setProducts(data.products || []);
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClickAnchor = (event, productId) => {
    setAnchorEl(event.currentTarget);
    setSelectedProduct(productId);
  };

  const handleCloseAnchor = () => {
    setAnchorEl(null);
    setSelectedProduct(null);
  };

  const toggleAddModal = () => {
    setIsAddModalOpen(!isAddModalOpen);
    if (!isAddModalOpen) {
      resetForm();
    }
  };

  const toggleUpdateModal = () => {
    setIsUpdateModalOpen(!isUpdateModalOpen);
    handleCloseAnchor();
  };

  const toggleViewModal = () => {
    setIsViewModalOpen(!isViewModalOpen);
    handleCloseAnchor();
  };

  const toggleDeleteModal = () => {
    setIsDeleteModalOpen(!isDeleteModalOpen);
    handleCloseAnchor();
  };

  const handleView = (product) => {
    setProductToView(product);
    toggleViewModal();
  };

  const handleUpdate = (product) => {
    setProductToUpdate(product);
    setValues({
      name: product.name || '',
      category: product.category || '',
      description: product.description || '',
      sku: product.sku || '',
      brand: product.brand || '',
      unit_price: product.unit_price || 0,
      image: null,
      is_active: product.is_active !== undefined ? product.is_active : true,
    });
    setImagePreview(product.image ? encodedImagesURI(product.image) : null);
    toggleUpdateModal();
  };

  const handleDelete = (productId) => {
    setProductToDelete(productId);
    toggleDeleteModal();
  };

  const handleConfirmDelete = async () => {
    setButtonLoading(true);
    try {
      const response = await fetch(`${BASE_URL}products/${productToDelete}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (response.ok) {
        setIsSuccessAlert(true);
        setSuccessMessage('Product deleted successfully');
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
          setIsSuccessAlert(false);
        }, 3000);
        getProducts();
        toggleDeleteModal();
      } else {
        setIsFailureAlert(true);
        setFailureMessage(data.message || 'Failed to delete product');
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
          setIsFailureAlert(false);
        }, 3000);
      }
    } catch (error) {
      setIsFailureAlert(true);
      setFailureMessage('Error deleting product');
      setIsPopupOpen(true);
      setTimeout(() => {
        setIsPopupOpen(false);
        setIsFailureAlert(false);
      }, 3000);
    } finally {
      setButtonLoading(false);
    }
  };

  const resetForm = () => {
    setValues({
      name: '',
      category: '',
      description: '',
      sku: '',
      brand: '',
      unit_price: 0,
      image: null,
      is_active: true,
    });
    setImagePreview(null);
  };

  const { values, touched, errors, handleBlur, handleChange, handleSubmit, setValues, setFieldValue } = useFormik({
    initialValues: {
      name: '',
      category: '',
      description: '',
      sku: '',
      brand: '',
      unit_price: 0,
      image: null,
      is_active: true,
    },
    onSubmit: async (values, actions) => {
      if (isAddModalOpen) {
        await handleAddProduct(values, actions);
      } else if (isUpdateModalOpen) {
        await handleUpdateProduct(values, actions);
      }
    },
  });

  const handleAddProduct = async (values, actions) => {
    setButtonLoading(true);
    try {
      const formData = new FormData();
      formData.append('company_id', company_id);
      formData.append('location_id', location_id);
      formData.append('name', values.name);
      formData.append('category', values.category);
      formData.append('description', values.description);
      formData.append('sku', values.sku);
      formData.append('brand', values.brand);
      formData.append('unit_price', values.unit_price);
      formData.append('is_active', values.is_active);
      if (values.image) {
        formData.append('image', values.image);
      }

      const response = await fetch(`${BASE_URL}products`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (response.ok) {
        setIsSuccessAlert(true);
        setSuccessMessage('Product added successfully');
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
          setIsSuccessAlert(false);
        }, 3000);
        getProducts();
        toggleAddModal();
        resetForm();
      } else {
        setIsFailureAlert(true);
        setFailureMessage(data.message || 'Failed to add product');
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
          setIsFailureAlert(false);
        }, 3000);
      }
    } catch (error) {
      setIsFailureAlert(true);
      setFailureMessage('Error adding product');
      setIsPopupOpen(true);
      setTimeout(() => {
        setIsPopupOpen(false);
        setIsFailureAlert(false);
      }, 3000);
    } finally {
      setButtonLoading(false);
      actions.setSubmitting(false);
    }
  };

  const handleUpdateProduct = async (values, actions) => {
    setButtonLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('category', values.category);
      formData.append('description', values.description);
      formData.append('sku', values.sku);
      formData.append('brand', values.brand);
      formData.append('unit_price', values.unit_price);
      formData.append('is_active', values.is_active);
      if (values.image) {
        formData.append('image', values.image);
      }

      const response = await fetch(`${BASE_URL}products/${productToUpdate._id}`, {
        method: 'PUT',
        body: formData,
      });
      const data = await response.json();
      if (response.ok) {
        setIsSuccessAlert(true);
        setSuccessMessage('Product updated successfully');
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
          setIsSuccessAlert(false);
        }, 3000);
        getProducts();
        toggleUpdateModal();
        resetForm();
      } else {
        setIsFailureAlert(true);
        setFailureMessage(data.message || 'Failed to update product');
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
          setIsFailureAlert(false);
        }, 3000);
      }
    } catch (error) {
      setIsFailureAlert(true);
      setFailureMessage('Error updating product');
      setIsPopupOpen(true);
      setTimeout(() => {
        setIsPopupOpen(false);
        setIsFailureAlert(false);
      }, 3000);
    } finally {
      setButtonLoading(false);
      actions.setSubmitting(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFieldValue('image', file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const paginatedProducts =
    !products || products.length === 0
      ? []
      : rowsPerPage === -1
      ? products
      : products.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      {isPopupOpen && (
        <Popup
          onClose={() => setIsPopupOpen(false)}
          alertType={isSuccessAlert ? 'success' : 'failure'}
          message={isSuccessAlert ? successMessage : failureMessage}
        />
      )}

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-4">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border border-gx-border bg-gx-canvas p-2 rounded-xl outline-0 text-gx-ink-soft"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={toggleAddModal}
          className="gx-cta"
        >
          Add Product
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center mt-20">
          <CircularProgress
            thickness={4}
            size={35}
            sx={{
              'svg circle': {
                stroke: 'url(#gradientColors)',
              },
            }}
          />
        </div>
      ) : products && products.length !== 0 ? (
        <Box className="bg-gx-surface rounded-gx border border-gx-border shadow-gx m-5 animate-fadeIn overflow-y-auto overflow-x-auto">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedProducts.map((product, index) => (
                <TableRow hover key={index}>
                  <TableCell>
                    {product.image ? (
                      <img
                        src={encodedImagesURI(product.image)}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gx-border rounded flex items-center justify-center text-gx-ink-soft">
                        No Image
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{product.category}</TableCell>
                  <TableCell>{product.sku}</TableCell>
                  <TableCell>{product.brand}</TableCell>
                  <TableCell>Rs. {product.unit_price}</TableCell>
                  <TableCell>
                    <div
                      className={`font-bold p-2 px-4 rounded-full text-center ${
                        product.is_active ? 'bg-green-700 text-white' : 'bg-red-700 text-white'
                      }`}
                    >
                      {product.is_active ? 'Active' : 'Inactive'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={(e) => handleClickAnchor(e, product._id)}>
                      <MoreVertIcon />
                    </IconButton>
                    <Menu anchorEl={anchorEl} open={openAnchor && selectedProduct === product._id} onClose={handleCloseAnchor}>
                      <MenuItem
                        onClick={() => {
                          handleView(product);
                        }}
                      >
                        <FontAwesomeIcon icon={faEye} className="mr-2" />
                        View
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleUpdate(product);
                        }}
                      >
                        <FontAwesomeIcon icon={faPen} className="mr-2" />
                        Edit
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleDelete(product._id);
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} className="mr-2" />
                        Delete
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  colSpan={8}
                  count={products.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  slotProps={{
                    select: {
                      inputProps: {
                        'aria-label': 'rows per page',
                      },
                      native: true,
                    },
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
      ) : (
        <div className="bg-gx-border w-full p-3 mt-10 rounded-lg m-10">
          <h3 className="text-xl font-bold text-center text-gx-ink-soft">No Products Found</h3>
        </div>
      )}

      {/* Add/Update Product Modal */}
      <CustomModal
        isModal={isAddModalOpen || isUpdateModalOpen}
        handleModal={() => {
          if (isAddModalOpen) {
            toggleAddModal();
          } else if (isUpdateModalOpen) {
            toggleUpdateModal();
          }
        }}
        size="2xl"
      >
        <h1 className="font-bold text-3xl text-gx-ink-soft p-5 border-b mx-2">
          {isAddModalOpen ? 'Add Product' : 'Update Product'}
        </h1>
        <form onSubmit={handleSubmit} className="text-gx-ink-soft m-5" autoComplete="off">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
            <div className="w-full">
              <label>
                Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="name"
                placeholder="Enter Product Name"
                value={values.name}
                className={`border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 ${
                  errors.name && touched.name ? 'border-red-600' : ''
                }`}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.name && errors.name ? <p className="text-red-500 pl-2 italic">{errors.name}</p> : null}
            </div>

            <div className="w-full">
              <label>
                Category <span className="text-red-600">*</span>
              </label>
              <select
                name="category"
                value={values.category}
                className={`border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 ${
                  errors.category && touched.category ? 'border-red-600' : ''
                }`}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              {touched.category && errors.category ? (
                <p className="text-red-500 pl-2 italic">{errors.category}</p>
              ) : null}
            </div>

            <div className="w-full sm:col-span-2">
              <label>Description</label>
              <textarea
                name="description"
                placeholder="Enter Product Description"
                value={values.description}
                className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0"
                onChange={handleChange}
                onBlur={handleBlur}
                rows={3}
              />
            </div>

            <div className="w-full">
              <label>
                SKU
              </label>
              <input
                type="text"
                name="sku"
                placeholder="Enter SKU (Optional)"
                value={values.sku}
                className={`border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 ${
                  errors.sku && touched.sku ? 'border-red-600' : ''
                }`}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.sku && errors.sku ? <p className="text-red-500 pl-2 italic">{errors.sku}</p> : null}
            </div>

            <div className="w-full">
              <label>Brand</label>
              <input
                type="text"
                name="brand"
                placeholder="Enter Brand"
                value={values.brand}
                className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0"
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>

            <div className="w-full">
              <label>
                Unit Price (Selling Price) <span className="text-red-600">*</span>
              </label>
              <input
                type="number"
                name="unit_price"
                placeholder="Enter Unit Price"
                value={values.unit_price}
                className={`border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 ${
                  errors.unit_price && touched.unit_price ? 'border-red-600' : ''
                }`}
                onChange={handleChange}
                onBlur={handleBlur}
                min="0"
                step="0.01"
              />
              {touched.unit_price && errors.unit_price ? (
                <p className="text-red-500 pl-2 italic">{errors.unit_price}</p>
              ) : null}
            </div>

            <div className="w-full">
              <label>Product Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0"
              />
              {imagePreview && (
                <div className="mt-2">
                  <img src={imagePreview} alt="Preview" className="w-32 h-32 object-cover rounded" />
                </div>
              )}
            </div>

            <div className="w-full flex items-center">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={values.is_active}
                  onChange={handleChange}
                  className="w-4 h-4"
                />
                Active
              </label>
            </div>
          </div>

          <div className="flex justify-end items-center pt-3 mt-3 border-t">
            <button
              type="button"
              onClick={() => {
                if (isAddModalOpen) {
                  toggleAddModal();
                } else if (isUpdateModalOpen) {
                  toggleUpdateModal();
                }
              }}
              className="gx-cta-secondary mr-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={buttonLoading}
              className="gx-cta disabled:opacity-50"
            >
              {buttonLoading ? 'Saving...' : isAddModalOpen ? 'Add Product' : 'Update Product'}
            </button>
          </div>
        </form>
      </CustomModal>

      {/* View Product Modal */}
      <CustomModal isModal={isViewModalOpen} handleModal={toggleViewModal} size="lg">
        {productToView && (
          <>
            <h1 className="font-bold text-3xl text-gx-ink-soft p-5 border-b mx-2">Product Details</h1>
            <div className="m-5 text-gx-ink-soft">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-semibold">Name:</label>
                  <p>{productToView.name}</p>
                </div>
                <div>
                  <label className="font-semibold">Category:</label>
                  <p>{productToView.category}</p>
                </div>
                <div>
                  <label className="font-semibold">SKU:</label>
                  <p>{productToView.sku}</p>
                </div>
                <div>
                  <label className="font-semibold">Brand:</label>
                  <p>{productToView.brand || 'N/A'}</p>
                </div>
                <div>
                  <label className="font-semibold">Unit Price:</label>
                  <p>Rs. {productToView.unit_price}</p>
                </div>
                <div className="col-span-2">
                  <label className="font-semibold">Description:</label>
                  <p>{productToView.description || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <label className="font-semibold">Status:</label>
                  <p>
                    <span
                      className={`font-bold p-2 px-4 rounded-full ${
                        productToView.is_active ? 'bg-green-700 text-white' : 'bg-red-700 text-white'
                      }`}
                    >
                      {productToView.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>
                {productToView.image && (
                  <div className="col-span-2">
                    <label className="font-semibold">Image:</label>
                    <img
                      src={encodedImagesURI(productToView.image)}
                      alt={productToView.name}
                      className="w-48 h-48 object-cover rounded mt-2"
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </CustomModal>

      {/* Delete Confirmation Modal */}
      <CustomModal isModal={isDeleteModalOpen} handleModal={toggleDeleteModal} size="md">
        <h1 className="font-bold text-3xl text-gx-ink-soft p-5 border-b mx-2">Delete Product</h1>
        <div className="m-5 text-gx-ink-soft">
          <p>Are you sure you want to delete this product? This action cannot be undone.</p>
        </div>
        <div className="flex justify-end items-center pt-3 mt-3 border-t p-5">
          <button
            onClick={toggleDeleteModal}
            className="gx-cta-secondary mr-3"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmDelete}
            disabled={buttonLoading}
            className="bg-red-600 text-white px-6 py-2 rounded-xl hover:bg-red-700 disabled:opacity-50"
          >
            {buttonLoading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </CustomModal>
    </>
  );
}

export default Products;

